import apiClient from '@/utils/apiClient';
import { analyzeToken, logTokenInfo, needsRenewal } from '@/utils/tokenUtils';
import React, { createContext, type ReactNode, useContext, useEffect } from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';


interface User {
    username: string;
    email?: string;
    givenName?: string;
    familyName?: string;
}

interface AuthContextType {
    user: User | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    signIn: () => void;
    signOut: () => Promise<void>;
    getAccessToken: () => Promise<string | null>;
    getCurrentAccessToken: () => string | null;
    getIdToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const auth = useOidcAuth();

    // Handle page refresh with expired token - simplified approach
    useEffect(() => {
        const handlePageLoad = async () => {
            // Only run this check once on initial load and only if we have obvious issues
            if (auth.isLoading) return;

            // Only check if we have a user but are not authenticated (inconsistent state)
            if (auth.user?.access_token && !auth.isAuthenticated) {
                console.warn('🚨 Inconsistent auth state detected on page load');
                try {
                    await auth.signinSilent();
                    console.debug('✅ Silent renewal on page load successful');
                } catch (silentError) {
                    console.error('❌ Silent renewal on page load failed:', silentError);
                    // Let the API calls handle the expired token naturally
                }
            }
        };

        // Run the check when auth state stabilizes, but with a delay to avoid race conditions
        if (!auth.isLoading) {
            setTimeout(handlePageLoad, 500);
        }
    }, [auth.isLoading]); // Only depend on isLoading to run once when it becomes false

    // Add token expiration monitoring with less aggressive checking
    useEffect(() => {
        let tokenCheckInterval: NodeJS.Timeout;

        const checkTokenExpiration = async () => {
            if (auth.user?.access_token) {
                try {
                    const tokenInfo = analyzeToken(auth.user.access_token);

                    // Only log token info in debug mode to reduce noise
                    if (process.env.NODE_ENV === 'development') {
                        logTokenInfo(auth.user.access_token, 'periodic check');
                    }

                    // If token is invalid or expired
                    if (!tokenInfo.isValid || tokenInfo.isExpired) {
                        console.warn('🚨 Token is invalid or expired, attempting background renewal');
                        try {
                            // Use background renewal to avoid UI disruption
                            await auth.signinSilent();
                            console.debug('✅ Background token renewal successful');
                        } catch (silentError) {
                            console.error('❌ Background token renewal failed:', silentError);
                            // Only clear auth if we're sure the token is completely invalid
                            // Let API calls handle the 401 errors gracefully
                        }
                        return;
                    }

                    // Only attempt proactive renewal if token expires within 2 minutes (reduced from 5)
                    if (needsRenewal(auth.user.access_token, 120)) {
                        console.debug('🔄 Token expiring soon, attempting background renewal');
                        try {
                            // Don't await - let this happen in background without blocking UI
                            auth.signinSilent().catch(silentError => {
                                console.warn('❌ Background proactive renewal failed:', silentError);
                            });
                        } catch (error) {
                            // Ignore errors for background renewal
                        }
                    }
                } catch (error) {
                    console.error('Error checking token expiration:', error);
                }
            }
        };

        // Handle the case where user is authenticated but token might be expired on page load
        if (auth.isAuthenticated && auth.user && !auth.isLoading) {
            // Check immediately but don't block UI
            setTimeout(checkTokenExpiration, 1000); // Delay initial check by 1 second
            // Increase interval to 5 minutes to reduce frequency
            tokenCheckInterval = setInterval(checkTokenExpiration, 300000); // Every 5 minutes instead of 1
        }

        return () => {
            if (tokenCheckInterval) {
                clearInterval(tokenCheckInterval);
            }
        };
    }, [auth.isAuthenticated, auth.user, auth.isLoading]);

    // Handle silent renewal events
    useEffect(() => {
        const handleSilentRenewError = (error: any) => {
            console.error('🚨 Silent renewal error:', error);
            // If silent renewal fails, we might need to force a full sign-in
            // But only if the user is currently authenticated (to avoid loops)
            if (auth.isAuthenticated) {
                console.warn('Silent renewal failed for authenticated user, may need manual sign-in');
            }
        };

        const handleUserLoaded = (user: any) => {
            console.debug('✅ User loaded/renewed:', {
                sub: user.profile?.sub,
                exp: user.expires_at ? new Date(user.expires_at * 1000).toISOString() : 'unknown'
            });
        };

        const handleUserUnloaded = () => {
            console.debug('👋 User unloaded');
        };

        const handleAccessTokenExpiring = () => {
            console.debug('⏰ Access token expiring, automatic renewal should trigger');
        };

        const handleAccessTokenExpired = () => {
            console.warn('🚨 Access token expired');
        };

        // Add event listeners if the auth object supports them
        if (auth.events) {
            auth.events.addSilentRenewError(handleSilentRenewError);
            auth.events.addUserLoaded(handleUserLoaded);
            auth.events.addUserUnloaded(handleUserUnloaded);
            auth.events.addAccessTokenExpiring(handleAccessTokenExpiring);
            auth.events.addAccessTokenExpired(handleAccessTokenExpired);

            return () => {
                auth.events.removeSilentRenewError(handleSilentRenewError);
                auth.events.removeUserLoaded(handleUserLoaded);
                auth.events.removeUserUnloaded(handleUserUnloaded);
                auth.events.removeAccessTokenExpiring(handleAccessTokenExpiring);
                auth.events.removeAccessTokenExpired(handleAccessTokenExpired);
            };
        }
    }, [auth]);

    const getUser = (): User | null => {
        if (!auth.user) return null;

        //print out in console the received auth.user
        console.debug('👤 AuthContext: Received user:', auth.user);
        const identities = JSON.parse(String(auth.user.profile?.identities) || '{}')
        return {
            username: (identities && identities.length > 0 && identities[0].userId) || auth.user.profile?.username || auth.user.profile?.sub || 'Unknown User',
            email: auth.user.profile?.email,
            givenName: auth.user.profile?.given_name,
            familyName: auth.user.profile?.family_name,
        };
    };

    const handleSignIn = () => {
        // Prevent multiple sign-in attempts
        const lastSignInAttempt = sessionStorage.getItem('lastSignInAttempt');
        const now = Date.now();

        if (lastSignInAttempt && (now - parseInt(lastSignInAttempt)) < 5000) {
            console.warn('Sign-in attempt blocked - too frequent');
            return;
        }

        // Store the current path as intended destination
        sessionStorage.setItem('intendedPath', window.location.pathname);
        sessionStorage.setItem('lastSignInAttempt', now.toString());

        try {
            auth.signinRedirect();
        } catch (error) {
            console.error('Error initiating sign-in:', error);
            sessionStorage.removeItem('lastSignInAttempt');
        }
    };

    const handleSignOut = async () => {
        try {
            await auth.signoutRedirect();
        } catch (error) {
            console.error('Error signing out:', error);
            // Fallback: clear local state and redirect manually
            auth.removeUser();
            window.location.href = '/';
        }
    };

    const getCurrentAccessToken = (): string | null => {
        // Synchronous method to get the current token without renewal attempts
        // This is used for status checking and should not trigger side effects
        return auth.user?.access_token || null;
    };

    const getAccessToken = async (): Promise<string | null> => {
        try {
            // Reduce logging noise in production
            if (process.env.NODE_ENV === 'development') {
                console.debug('🔑 AuthContext: Getting access token');
            }

            // Simplified token retrieval - trust the OIDC library more
            if (!auth.user?.access_token) {
                return null;
            }

            // Check if current token is valid and not expired
            const tokenInfo = analyzeToken(auth.user.access_token);

            if (!tokenInfo.isValid) {
                console.warn('❌ AuthContext: Token is invalid');
                return null;
            }

            if (tokenInfo.isExpired) {
                console.warn('❌ AuthContext: Token is expired, attempting silent renewal');
                try {
                    await auth.signinSilent();
                    // Return the new token if available, otherwise null
                    if (auth.user?.access_token) {
                        if (process.env.NODE_ENV === 'development') {
                            logTokenInfo(auth.user.access_token, 'after silent renewal');
                        }
                        return auth.user.access_token;
                    }
                    return null;
                } catch (silentError) {
                    console.error('❌ AuthContext: Silent renewal failed:', silentError);
                    return null;
                }
            }

            // Return current token without proactive renewal to reduce UI disruption
            // Let the background interval handle proactive renewals
            if (process.env.NODE_ENV === 'development') {
                console.debug('✅ AuthContext: Access token retrieved successfully');
                logTokenInfo(auth.user.access_token, 'retrieved');
            }

            return auth.user.access_token;
        } catch (error) {
            console.error('❌ AuthContext: Error getting access token:', error);
            return null;
        }
    };

    // Add a method to get ID token for AWS credentials
    const getIdToken = async (): Promise<string | null> => {
        try {
            console.debug('🔑 AuthContext: Getting ID token for AWS credentials');

            // Add retry logic for race conditions
            let retryCount = 0;
            const maxRetries = 3;
            const retryDelay = 100; // 100ms

            while (retryCount < maxRetries) {
                if (!auth.user) {
                    console.debug(`❌ AuthContext: No user object available (attempt ${retryCount + 1}/${maxRetries})`);
                    if (retryCount < maxRetries - 1) {
                        await new Promise(resolve => setTimeout(resolve, retryDelay));
                        retryCount++;
                        continue;
                    }
                    return null;
                }

                if (!auth.user.id_token) {
                    console.debug(`❌ AuthContext: User object exists but no id_token (attempt ${retryCount + 1}/${maxRetries})`);
                    console.debug('User object keys:', Object.keys(auth.user));
                    if (retryCount < maxRetries - 1) {
                        await new Promise(resolve => setTimeout(resolve, retryDelay));
                        retryCount++;
                        continue;
                    }
                    return null;
                }

                // We have a token, break out of retry loop
                break;
            }

            // Double-check we have a token after retries
            if (!auth.user?.id_token) {
                console.error('❌ AuthContext: Still no ID token after retries');
                return null;
            }

            // Check if current token is valid and not expired
            const tokenInfo = analyzeToken(auth.user.id_token);

            if (!tokenInfo.isValid) {
                console.warn('❌ AuthContext: ID token is invalid');
                return null;
            }

            if (tokenInfo.isExpired) {
                console.warn('❌ AuthContext: ID token is expired, attempting silent renewal');
                try {
                    await auth.signinSilent();
                    // After silent renewal, get the new token with retry logic
                    let renewalRetries = 0;
                    while (renewalRetries < 3) {
                        if (auth.user?.id_token) {
                            logTokenInfo(auth.user.id_token, 'ID token after silent renewal');
                            return auth.user.id_token;
                        }
                        await new Promise(resolve => setTimeout(resolve, 100));
                        renewalRetries++;
                    }
                    console.error('❌ AuthContext: No ID token after silent renewal and retries');
                    return null;
                } catch (silentError) {
                    console.error('❌ AuthContext: Silent renewal failed:', silentError);
                    return null;
                }
            }

            console.debug('✅ AuthContext: ID token retrieved successfully');
            logTokenInfo(auth.user.id_token, 'ID token retrieved');

            return auth.user.id_token;
        } catch (error) {
            console.error('❌ AuthContext: Error getting ID token:', error);
            return null;
        }
    };

    // Configure API client and AgentCore service with token provider
    useEffect(() => {
        // Only configure token providers when auth state is stable
        if (!auth.isLoading) {
            console.debug('🔧 AuthContext: Configuring API client and AgentCore service token providers');
            // API client uses access token for API Gateway calls
            apiClient.setTokenProvider(getAccessToken);
        }

        // Log authentication state changes
        console.debug('Auth state changed:', {
            isLoading: auth.isLoading,
            isAuthenticated: auth.isAuthenticated,
            hasUser: !!auth.user,
            hasError: !!auth.error
        });

        if (auth.error) {
            console.error('OIDC Auth Error:', auth.error);
        }
    }, [auth.isLoading, auth.isAuthenticated, auth.user, auth.error]);

    const value: AuthContextType = {
        user: getUser(),
        isLoading: auth.isLoading,
        isAuthenticated: auth.isAuthenticated,
        signIn: handleSignIn,
        signOut: handleSignOut,
        getAccessToken,
        getCurrentAccessToken,
        getIdToken,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};